import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getToken } from "firebase/messaging";
import {
    Box,
    CssBaseline,
    Toolbar,
    Popover,
    Button,
    Modal,
    Checkbox,
    FormControlLabel
} from '@mui/material';
import SmartXIntegration from './widgets/SmartXIntegration';
import Dashboard from './Dashboard';
import Platforms from './Platforms';
import SalesReport from './SalesReport';
import ClicksReport from './ClicksReport';
import NavigationBar from './widgets/NavigationBar';
import UserMenu from './widgets/UserMenu';
import IPChecker from './IPChecker';
import GClidDecoder from './GClidDecoder';
import URLGenerator from './URLGenerator';
import MyBlacklist from './MyBlacklist';
import FullBlacklist from './FullBlacklist';
import SalesFunnel from './SalesFunnel';
import Integrations from './Integrations';
import IOSSafariInstallBanner from './widgets/IOSSafariInstallBanner';  
import Cookies from 'js-cookie';
import { registerPushToken } from '../services/push';

function Home({ messaging }) {

    const { t } = useTranslation();

    const menuItems = [
        { 
            title: t('menu.dashboard'), 
            component: ()=>(<Dashboard user={user} />), 
        },
        { 
            title: t('menu.platforms'), 
            component: ()=>(
                <Platforms 
                    user={user} 
                    setUser={setUser} 
                    handleMenuSelection={handleMenuSelection}
                    />
                )
        },
        { 
            title: t('menu.reports'),
            subOptions: [
                { 
                    title: t('menu.reports_clicks'),
                    subOptionId: 0,
                    menuIndex: 2,
                    component: ()=>(<ClicksReport user={user} />),
                },
                { 
                    title: t('menu.reports_conversions'),
                    subOptionId: 1,
                    menuIndex: 2,
                    component: ()=>(<SalesReport user={user} />),
                },
                {                
                    title: t('menu.reports_sales_funnel'),
                    subOptionId: 2,
                    menuIndex: 2,
                    component: ()=>(<SalesFunnel user={user} />),
            }
            ], 
        },
        { 
            title: t('menu.tracking'),
            subOptions: [
                { 
                    title: t('menu.tracking_IP'),
                    subOptionId: 3,
                    menuIndex: 3,
                    component: ()=>(<IPChecker />),
                },
                {
                     title: t('menu.tracking_gclid_decoder'),
                     subOptionId: 4,
                     menuIndex: 3,
                     component: ()=>(<GClidDecoder />),
                },
            ], 
        },
        { 
            title: t('menu.blacklist'),
            subOptions: [
                { 
                    title: t('menu.blacklist_my_blacklist'),
                    subOptionId: 5,
                    menuIndex: 4,
                    component: ()=>(<MyBlacklist user={user} />),
                },
                { 
                    title: t('menu.blacklist_smartx_blacklist'),
                    subOptionId: 6,
                    menuIndex: 4,
                    component: ()=>(<FullBlacklist user={user} />),
                },
            ],
        },
        {
            title: t('menu.url_builder'),
            component: () => (<URLGenerator/>),
        },
        {
            title: t('menu.integrations'),
            component: () => (<Integrations user={user} setUser={setUser}/>),
        }
    ];

    const location = useLocation();
    const navigate = useNavigate();
    const { user: initialUser } = location.state || {};
    const [user, setUser] = useState(initialUser ?? JSON.parse(localStorage.getItem('user')) );
    const [selectedMenuIndex, setSelectedMenuIndex] = useState(0);
    const [showUserProfile, setShowUserProfile] = useState(false);
    const [selectedOption, setSelectedOption] = useState(menuItems[0]);
    const [subOptions, setSubOptions] = useState([]);
    const [showModal, setShowModal] = useState(!Cookies.get('showScriptModal'));
    const [hideModal, setHideModal] = useState(false);
    const [subOptionsAnchorEl, setSubOptionsAnchorEl] = useState(null);

     // Navigate to /login if no user is set or try to load from localStorage
    useEffect(() => {
        if (!user) {
            const storedUser = JSON.parse(localStorage.getItem('user'));
            if (storedUser) {
                setUser(storedUser);
                navigate('/home', { state: { user: storedUser } });
            } else {
                navigate('/login');
            }
        } else {
            askForFirebasePushPermission();
        }
    }, [user, navigate]);

    const askForFirebasePushPermission = async () => {
        const vapidKey = process.env.REACT_APP_VAPID_KEY;
      
        if (!vapidKey) {
          console.error("Missing VAPID key! Check .env file.");
          return;
        }
      
        if (Notification.permission === 'default') {
          const permission = await Notification.requestPermission();
          if (permission === 'granted') {
            const token = await getToken(messaging, { vapidKey });
            registerToken(token)
          } else {
            console.warn('Notification permission denied:', permission);
          }
        } else if (Notification.permission === 'granted') {
          const token = await getToken(messaging, { vapidKey });
          registerToken(token)
        } else {
          console.warn('Notification blocked by user (no prompt shown)');
        }
    }

    const registerToken = async (pushToken) => {
        if (user.pushTokens?.includes(pushToken)) {
            return;
        }

        if (!pushToken) {
            console.error('No push token available');
            return;
        }
        const result = await registerPushToken(user._id, pushToken);
        if (result) {
          console.log("Push token registered");
        } else {
          console.error('Failed to register push token');
        }
    }

    const handleMenuSelection = (menuIndex, subOption, event) => {
        if (subOption) {
            setSelectedOption(subOption);
            setSelectedMenuIndex(menuIndex);
            setSubOptions([]);
            setSubOptionsAnchorEl(null);
            return;
        }
    
        const selectedOption = menuItems[menuIndex];
        if (selectedOption.subOptions?.length > 0) {
            setSubOptions(selectedOption.subOptions);
            console.log("SubOptions element: ", event);
            setSubOptionsAnchorEl(event?.currentTarget ?? null);
        } else {
            setSelectedOption(selectedOption);
            setSelectedMenuIndex(menuIndex);
            setSubOptionsAnchorEl(null);
        }
    };

    const handleCloseModal = () => {
        if (hideModal) {
            Cookies.set('showScriptModal', 'true', { expires: 365 }); // Save preference in cookies for 1 year
        }
        setShowModal(false);
    };

    return (
        user && user?.notificationId ? (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            <Modal
            sx={{ display: { xs: 'none', md: 'flex' } }}
                open={showModal}
                onClose={handleCloseModal}
                aria-labelledby="modal-title"
                aria-describedby="modal-description"
            >
                <SmartXIntegration user={user} >
                    <FormControlLabel
                        control={
                            <Checkbox
                            checked={hideModal}
                            onChange={(e) => setHideModal(e.target.checked)}
                            />
                        }
                        label={t('modal_config.dont_show_again')}
                        sx={{ mt: 2, marginTop: 5 }}
                    />
                    <Button
                        fullWidth
                        variant="contained"
                        sx={{ mt: 2 }}
                        onClick={handleCloseModal}
                    >
                        {t('modal_config.close_button')}
                    </Button>
                </SmartXIntegration>                    
            </Modal>

            {/* App Bar */}
            <NavigationBar 
                user={user} 
                menuItems={menuItems} 
                onMenuSelected={handleMenuSelection}
                selectedMenuIndex={selectedMenuIndex}
                subOptions={subOptions}
                selectedSubOption={selectedOption}
                onUserProfileSelect={() => { setShowUserProfile(true) }}
            />

            {/* Main Content */}
            <Box 
                component="main"
                sx={{ 
                    marginTop: '40px', 
                    flexGrow: 1, 
                    width: { sm: '100%px' } 
                }}
            >
                <Toolbar />
                {selectedOption.component()}
            <Popover
                open={Boolean(subOptionsAnchorEl) && subOptions.length > 0}
                anchorEl={subOptionsAnchorEl}
                onClose={() => {
                    setSubOptions([]);
                    setSubOptionsAnchorEl(null);
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                sx={{ display: { xs: 'none', md: 'block' } }}
            >
                <Box sx={{ padding: 2 }}>
                    {subOptions?.map((option, index) => (
                        <Button
                        key={index}
                        variant="text"
                        onClick={() => {
                            setSelectedOption(option);
                            setSelectedMenuIndex(option.menuIndex);
                            setSubOptions([]);
                            setSubOptionsAnchorEl(null);
                        }}
                        sx={{ display: 'block', marginBottom: 1 }}
                        >
                        {option.title}
                        </Button>
                    ))}
                </Box>
            </Popover>
            </Box>
            <Popover
                sx={{ marginTop: 8, marginRight: 2 }}
                onClose={() => { setShowUserProfile(false) }}   
                open={showUserProfile}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right"
                }}
            >
                <UserMenu 
                    user={user} 
                    setUser={setUser}
                    onItemSelected={(item) => {
                        setSelectedOption(item);
                        setSelectedMenuIndex(-1);
                        setShowUserProfile(false);
                    }}
                    onAccountLogout={() => {
                        localStorage.removeItem('user');
                        setUser(null)
                    }}/>
            </Popover>
            <IOSSafariInstallBanner />
        </Box>
        ) : null
    );
}

export default Home;